import { supportsTouchEvents } from 'detect-it'

import { clamp } from '@/utils/maths'

export default {
	name: 'slideshow',
	component(
		props = {
			groupBy: 1,
			autoPlayInterval: undefined,
			disableLg: false,
			loop: false,
			displayChildrenCountMobile: -1,
			displayChildrenCountDesktop: -1
		}
	) {
		const { groupBy, autoPlayInterval, disableLg, loop, displayChildrenCountMobile, displayChildrenCountDesktop } = props
		const wrapper = this.$refs.wrapper

		const dots = []
		if (this.$refs.dots) {
			dots.push(...this.$refs.dots?.children)
		}
		const prevButton = this.$refs.prevButton
		const nextButton = this.$refs.nextButton

		return {
			index: 0,
			maxIndex: 0,
			x: 0,
			destX: 0,
			pages: 0,
			easing: 0.11,
			autoPlayEasing: 0.08,
			hasTouched: false,
			loop,
			buttonState: -1,
			interval: undefined,
			intersectionObserver: {} as IntersectionObserver,
			autoPlayStopped: false,
			containerBound: {},
			bounds: [] as { left: number; width: number }[],
			children: [],
			enabled: true,
			wrapper,
			displayChildrenCount: -1,

			handleTouchStart() {
				this.stopAutoPlay()
				this.hasTouched = true
			},

			handlePrev() {
				this.stopAutoPlay()
				const count = this.bounds.length
				let nextIndex: number

				// If the index is at the last item and more slides are visible at once
				// this will allow the user to go back instantly, without having to click previous for n times
				// till the index reaches a slide that is out of frame
				if (this.index === this.maxIndex && this.displayChildrenCount !== -1) {
					const _index = this.index - this.displayChildrenCount - (groupBy - 1)
					if (_index <= 0) {
						nextIndex = 0
					} else {
						nextIndex = _index
					}
				} else {
					nextIndex = this.index >= groupBy ? this.index - groupBy : this.loop ? this.index + count - groupBy : 0
				}

				this.scrollTo(nextIndex)
			},

			handleNext(forceLoop = false) {
				if (!forceLoop) this.stopAutoPlay()
				const count = this.bounds.length
				let nextIndex: number

				if (this.index !== this.maxIndex && this.displayChildrenCount !== -1 && this.index + this.displayChildrenCount >= this.maxIndex) {
					nextIndex = this.maxIndex
				} else {
					nextIndex = this.index < count - groupBy ? this.index + groupBy : this.loop || forceLoop ? (this.index + groupBy) % count : count - groupBy
				}

				this.scrollTo(nextIndex)
			},

			handleNavChange(index: number, instant: boolean) {
				this.stopAutoPlay()
				this.scrollTo(index, instant)
			},

			stopAutoPlay() {
				clearTimeout(this.interval)
				this.autoPlayStopped = true
			},

			scrollTo(_index: number, instant = false) {
				let index = _index
				if (!this.maxIndex || !this.containerBound) return
				if (_index === -1) {
					index = this.maxIndex
				}

				this.hasTouched = false

				this.index = clamp(index, 0, this.maxIndex)

				const boundsIndex = index * groupBy

				this.destX = Math.min(
					this.bounds[boundsIndex].left,
					this.bounds[this.maxIndex].left + this.bounds[this.maxIndex].width - this.containerBound.width
				)

				if (wrapper && supportsTouchEvents) {
					wrapper.scrollTo({ left: this.destX, behavior: instant ? 'instant' : 'smooth' })
				} else if (wrapper) {
					wrapper.classList.remove('snap-x')
				}

				this.buttonState = this.index === 0 ? -1 : this.index >= this.maxIndex - 1 ? 1 : 0
			},

			scrollToRatio(ratio: number, instant = false) {
				if (!this.maxIndex || !this.containerBound) return

				this.hasTouched = false

				// Calculate the target index based on the ratio
				const targetIndex = Math.round(clamp(ratio, 0, 1) * this.maxIndex)

				this.index = clamp(targetIndex, 0, this.maxIndex)

				const boundsIndex = this.index * groupBy
				this.destX = Math.min(
					this.bounds[boundsIndex].left,
					this.bounds[this.bounds.length - 1].left + this.bounds[this.bounds.length - 1].width - this.containerBound.width
				)

				if (wrapper) {
					wrapper.scrollTo({ left: this.destX, behavior: instant ? 'instant' : 'smooth' })
					wrapper.classList.remove('snap-x')
				}

				this.buttonState = this.index === 0 ? -1 : this.index >= this.maxIndex - 1 ? 1 : 0
			},

			tick() {
				if (!supportsTouchEvents) {
					this.x += (this.destX - this.x) * (!this.autoPlayStopped && autoPlayInterval ? this.autoPlayEasing : this.easing)

					if (wrapper) {
						wrapper.scrollTo({ left: this.x, behavior: 'instant' })
					}
				}
			},

			init() {
				this.children = wrapper.children

				if (prevButton && !this.loop) {
					prevButton.disabled = Boolean(this.index === 0)
				}

				const getItemsBoundingRect = () => {
					const observer = new IntersectionObserver(entries => {
						// we can't rely on this.containerBound.left in case the slideshow is in an animated container, such as a slideout
						if (this.containerBound?.left !== entries[0].left) {
							this.containerBound = wrapper.getBoundingClientRect()
						}

						this.bounds = entries
							.map(entry => ({
								left: entry.boundingClientRect.left - this.containerBound.left + this.x,
								width: entry.boundingClientRect.width
							}))
							.sort(rect => rect.left)

						this.maxIndex = this.bounds.length - 1

						// removed containerBound.width to reach last item
						const delta = this.bounds[this.maxIndex].left + this.bounds[this.maxIndex].width
						for (let i = this.maxIndex; i >= 0; i--) {
							const rect = this.bounds[i]
							if (rect.left - rect.width < delta) {
								this.maxIndex = i
								break
							}
						}

						if (this.bounds[this.bounds.length - 1].left + this.bounds[this.bounds.length - 1].width < this.$el.offsetWidth) {
							this.enabled = false
						}

						observer.disconnect()
					})

					if (this.children) {
						for (const item of this.children) {
							if (!item.className.match(/\b\w*hidden\w*\b/g)) observer.observe(item)
						}
					}

					if (dots.length > 0 || (prevButton && nextButton)) {
						this.$watch('index', value => {
							if (prevButton && nextButton && !this.loop) {
								prevButton.disabled = Boolean(value === 0)
								nextButton.disabled = Boolean(value >= this.maxIndex)
							}
						})
					}
				}

				// update index based on intersection observer
				const handleChildEnter = (elements: IntersectionObserverEntry[]) => {
					const element = elements[0]
					if (element.isIntersecting && this.hasTouched) {
						this.index = Math.floor(element.target.__i / groupBy)
					}
				}

				this.childrenIntersectionObserver = new IntersectionObserver(handleChildEnter, {
					root: this.$el,
					threshold: 0.55
				})

				Array.from(this.children).forEach((el, index) => {
					if (el) {
						el.__i = index
						this.childrenIntersectionObserver.observe(el)
					}
				})

				this.$useRaf(() => {
					this.tick()
				})

				// update sizing when dom is displayed for the first time
				const handleEnter = (elements: IntersectionObserverEntry[]) => {
					const element = elements[0]
					if (element.isIntersecting) {
						if (this.intersectionObserver) this.intersectionObserver.disconnect()

						this.$useResizeObserver(wrapper, rect => {
							this.containerBound = rect || wrapper.getBoundingClientRect()
							if (rect.width >= 1024) {
								this.displayChildrenCount = displayChildrenCountDesktop
							} else {
								this.displayChildrenCount = displayChildrenCountMobile
							}

							getItemsBoundingRect()
							this.scrollTo(this.index)
						})
					}
				}

				this.intersectionObserver = new IntersectionObserver(handleEnter, {})
				this.intersectionObserver.observe(this.$el)

				if (autoPlayInterval && !this.autoPlayStopped) {
					this.interval = setInterval(() => {
						this.handleNext(true)
					}, autoPlayInterval)
				}
			},

			destroy() {
				clearInterval(this.interval)
				this.childrenIntersectionObserver?.disconnect()
				this.intersectionObserver?.disconnect()
			}
		}
	}
}
